import { CostBasisPosition } from '@interfaces/cost-basis/cost-basis.service.interface';
import { FraudOutcome } from '@interfaces/fraud-detector/fraud-detector.interface';
import {
  CO6ReceiptDTO,
  InternalTransferReceiptDTO,
  InvestmentObjectives,
  IReceiptDTO,
  ISelectedClientAccountsState,
} from '@interfaces/models';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import { OneTimePasscodeStatus } from '@interfaces/one-time-passcode/otp.interface';
import {
  DecedentInfoState,
  FeatureToggleState,
  GiftingState,
  InternalTransferState,
  IOpenAccountState,
  TriageState,
} from '@interfaces/state';
import { InheritedIraState } from '@interfaces/state/inherited-ira/inherited-ira.interface';
import { BeneficiariesState } from '@store/reducers/beneficiaries/beneficiaries.reducer';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { TCFTrustState } from '@store/reducers/tcf-trust/tcf-trust.reducer';
import { TrustState } from '@store/reducers/trust/trust.reducer';
import { TrustTaxInfoState } from '@store/reducers/trust-tax-info/trust-tax-info.reducer';
import { ConsumerPayloadsType } from '@vanguard/fmf-common/dist/models/types/consumer-payloads';
import { VgimAddress } from 'vg-interaction-model';
import { IPersonalPhoneState } from 'vg-interaction-model/dist/lib/features/personal-phone/store/reducers/personal-phone.reducer';

export interface S3FileInformation {
  filename: string;
  fileExtension: string;
}

export declare enum RecaptchaSimulatorOptions {
  DEFAULT = 'DEFAULT',
  PASS = 'PASS',
  ERROR = 'ERROR',
  FAIL = 'FAIL',
}
export interface RecaptchaMockResult {
  result: RecaptchaSimulatorOptions;
  errorMessage?: string[];
}
export interface FMFResponse {
  action: string;
  transactionId: string;
  tokens: ConsumerPayloadsType;
}
export interface W9TermsAcceptedDates {
  documentsAndElectronicConsentAcceptedDate: string;
  backupWithholdingAcceptedDate: string;
  signApplicationAcceptedDate: string;
  ssn: string;
}

export interface RegularISubmit {
  transactionType:
    | Co6TransactionTypes.gifting
    | Co6TransactionTypes.inheritance
    | Co6TransactionTypes.minor
    | Co6TransactionTypes.inheritedIra;
  config: ISubmitConfig;
  receiptDTO: IReceiptDTO | CO6ReceiptDTO;
  fmfResponse?: FMFResponse;
  oaFMFResponse?: FMFResponse;
  mockRecaptchaLevel?: RecaptchaMockResult;
  mockFraudDetectorLevel?: FraudOutcome;
  otpVerificationStatus: OneTimePasscodeStatus;
  daEnrollPageHtml?: string;
  w9TermsAcceptedDates?: W9TermsAcceptedDates;
  multiInheritanceBackupWithholdingSections?: MultiInheritanceBackupWithholdingSectionPayload;
}

export interface InternalTransferSubmit {
  transactionType: Co6TransactionTypes.internalTransfer | Co6TransactionTypes.vanguardCharitable;
  itConfig: InternalTransferSubmitConfig;
  receiptDTO: InternalTransferReceiptDTO;
  fmfResponse?: FMFResponse;
  oaFMFResponse?: FMFResponse;
  mockRecaptchaLevel?: RecaptchaMockResult;
  mockFraudDetectorLevel?: FraudOutcome;
  otpVerificationStatus: OneTimePasscodeStatus;
  w9TermsAcceptedDates?: W9TermsAcceptedDates;
}

export enum Co6TransactionTypes {
  minor = 'MINOR',
  gifting = 'GIFTING',
  inheritance = 'INHERITANCE',
  internalTransfer = 'INTERNAL_TRANSFER',
  vanguardCharitable = 'VANGUARD_CHARITABLE',
  inheritedIra = 'INHERITED_IRA',
}

export type ISubmit = RegularISubmit | InternalTransferSubmit;

export interface GiftingSubmitConfigPayload extends GiftingState {
  selectedPositions: CostBasisPosition[];
}

export interface ISubmitConfig {
  base64Pdf?: string;
  personalData: {
    ownerName: string;
    segment: string;
    dateOfBirth?: string;
    isSpouse?: string;
    accountNumber?: string;
    accountId?: string;
    citizenshipStatus?: string;
    countryOfCitizenship?: string;
  };
  decedentInfo: SubmitDecedentInfoState;
  featureToggles: FeatureToggleState;
  triage: TriageState;
  openAccountState: IOpenAccountState;
  selectedAccs: ISelectedClientAccountsState;
  gifting?: GiftingSubmitConfigPayload;
  inheritedIraState?: InheritedIraState;
  userType?: string;
  estateInfo?: EstateState;
  trust?: TrustState;
  trustTaxInfo?: TrustTaxInfoState;
  tcfTrust?: TCFTrustState;
  employment?: {
    associations: VgEmploymentState['associations'];
    employmentStatus: VgEmploymentState['employmentStatus'];
  };
  mailingAddress?: VgimAddress;
  streetAddress?: VgimAddress;
  phone?: IPersonalPhoneState;
  beneficiaries?: BeneficiariesState;
  caseInformationFiles?: S3FileInformation[];
  filenetCaptureFiles?: S3FileInformation[];
  investmentObjectives?: InvestmentObjectives;
  taxDocumentation?: TaxDocumentation;
  termsAcceptance?: TermsAcceptance;
  backupWithholdingSignatureName?: string;
  backupWithholdingAnswer?: 'yes' | 'no';
}

export interface MultiInheritanceBackupWithholdingTrustGroup {
  trustName: string;
  ein: string;
  grantorName: string;
  grantorSsn: string;
  isGrantorTrust: boolean;
  taxClassification: string;
}

export interface MultiInheritanceBackupWithholdingEstateGroup {
  estateName: string;
  tin: string;
}

export interface MultiInheritanceBackupWithholdingSection {
  taxClassification: string;
  backupWithholdingAnswer?: 'yes' | 'no';
  backupWithholdingSignatureName?: string;
}

export interface MultiInheritanceBackupWithholdingSectionPayload {
  hasIraSection: boolean;
  hasIndividualJointSection: boolean;
  trustGroups: MultiInheritanceBackupWithholdingTrustGroup[];
  estateGroups: MultiInheritanceBackupWithholdingEstateGroup[];
  ssn: string;
  sections: MultiInheritanceBackupWithholdingSection[];
}

export interface TermsAcceptance {
  documentsAndElectronicConsent?: string | null;
  spouseCertification?: string | null;
  beneficiaryCertification?: string | null;
  trusteeCertification?: string | null;
  signApplication?: string | null;
  [key: string]: string | null | undefined;
}

export interface SubmitDecedentInfoState extends DecedentInfoState {
  accountType?: string;
}

export interface InternalTransferSubmitConfig {
  personalData: { ownerName: string; segment: string };
  featureToggles: FeatureToggleState;
  internalTransfer: InternalTransferState;
  openAccountState: IOpenAccountState;
  selectedPositions: CostBasisPosition[];
  base64Pdf?: string;
  caseInformationFiles?: S3FileInformation[];
  filenetCaptureFiles?: S3FileInformation[];
}

export interface SubmitLoggerConfig {
  userPoid: string;
  userType: string;
  transactionType: string;
  segment: string;
}

export interface SubmitLoggerConfig {
  userPoid: string;
  userType: string;
  transactionType: string;
  segment: string;
  transferFrom?: string;
  transferTo?: string;
}

export interface ITSubmitLoggerConfig {
  userPoid: string;
  userType: string;
  transactionType: string;
  segment: string;
  amountTransacted: string;
  transferFrom: string;
  transferTo: string;
  transferMethod: string;
  transferType: string;
}

export type SubmitLoggerInterface = SubmitLoggerConfig | ITSubmitLoggerConfig;

export interface CO6Response {
  status: number;
  data: STPResponse;
}

export interface AppianCWPSuccessResponse {
  success: boolean;
  appianSuccess: AppianSuccessResponse;
}

export interface AppianCWPErrorResponse {
  success: boolean;
  appianFailed: any;
}
export interface STPResponse {
  transactId: string;
}

export type SubmitResponse = AppianCWPSuccessResponse | STPResponse;

export interface SubmitErrorResponse {
  eventId: string;
}

export interface AppianSuccessResponse {
  statusCode: number;
  bpmCaseId?: string[] | undefined;
  bpmEnvelopeId?: number;
  createDateTime?: string;
}

export type TaxDocumentationStatus =
  | 'taxFileUploaded'
  | 'mailingForm'
  | 'beneficiarySubmitted'
  | 'noFormNeeded'
  | 'notValid';

export interface TaxDocumentation {
  status?: TaxDocumentationStatus;
  formName?: string;
}
